"use client";

import { useActionState } from "react";
import type { FormState } from "../actions";

/*
 * The reset request form and its confirmation.
 *
 * Owns its own form rather than reusing <AuthForm>: that component calls
 * useActionState itself, so nesting them meant the submission drove the inner
 * state and this component's `done` never fired -- the confirmation would
 * simply never appear.
 *
 * The confirmation is worded so it says nothing about whether the address
 * exists ("if that address has an account"). The design states the response is
 * identical either way, and requestPasswordReset returns void precisely so this
 * screen cannot become the leak.
 */
export function ResetRequested({
  action,
}: {
  action: (prev: FormState, form: FormData) => Promise<FormState>;
}) {
  const [state, formAction, pending] = useActionState(action, {});

  if (state.done) {
    return (
      <p style={{
        fontSize: 14, padding: "12px 14px", lineHeight: 1.6, margin: 0,
        background: "var(--color-accent-100)", color: "var(--color-accent-800)",
        border: "1px solid var(--color-accent-400)",
      }}>
        If that address has an account, a reset link is on its way. Check spam if
        it does not arrive in a few minutes.
      </p>
    );
  }

  return (
    <form action={formAction} noValidate>
      <div className="field" style={{ marginBottom: 14 }}>
        <label htmlFor="email">Email</label>
        <input className="input" id="email" name="email" type="email"
               autoComplete="email" required />
      </div>

      {state.error && (
        <p role="alert" style={{
          fontSize: 13, marginTop: 14, marginBottom: 0, padding: "8px 10px",
          color: "var(--color-danger-900)", background: "var(--color-danger-100)",
          border: "1px solid var(--color-danger-500)",
        }}>
          {state.error}
        </p>
      )}

      <button className="btn btn-primary btn-block" type="submit" disabled={pending}
              style={{ marginTop: 18 }}>
        {pending ? "Sending…" : "Send reset link"}
      </button>
    </form>
  );
}
